import React, { useEffect, useState } from "react";
import { Phone, PhoneIncoming, PhoneOutgoing, CheckCircle2, Flame, CalendarCheck, ArrowUp, ArrowDown, Clock, MapPin, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { DirectionBadge, LiveDot, ScoreBadge, LANG_NAMES, fmtDur, fmtTime } from "@/lib/format";

function KPICard({ icon: Icon, label, value, sub, trend, color }) {
  return (
    <Card className="p-5 border shadow-none hover:shadow-md transition-shadow" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
        {trend != null && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${trend >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="text-2xl font-bold font-display text-slate-900 dark:text-white">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
        {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/live-calls").then((r) => setLive(r.data)).catch(() => {});
    api.get("/activity").then((r) => setActivity(r.data)).catch(() => {});
    const t = setInterval(() => api.get("/live-calls").then((r) => setLive(r.data)).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, []);

  if (!data) return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
  );

  const k = data.kpis;
  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Unified inbound + outbound calling overview" />

      <OnboardingChecklist />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard icon={Phone} label="Total Calls Today" value={k.total_calls} trend={k.total_trend} color="bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15" />
        <KPICard icon={PhoneIncoming} label="Inbound Today" value={k.inbound_calls} sub={`${k.inbound_answered_pct}% answered`} color="bg-purple-100 text-purple-600 dark:bg-purple-500/15" />
        <KPICard icon={PhoneOutgoing} label="Outbound Today" value={k.outbound_calls} sub={`${k.outbound_answer_rate}% answer rate`} color="bg-blue-100 text-blue-600 dark:bg-blue-500/15" />
        <KPICard icon={CheckCircle2} label="Qualified Today" value={k.qualified_today ?? k.qualified_leads} sub={`${k.qualified_leads} total · ${k.qualification_rate}%`} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15" />
        <KPICard icon={Flame} label="Hot Leads" value={k.hot_leads} sub="needs action" color="bg-red-100 text-red-600 dark:bg-red-500/15" />
        <KPICard icon={CalendarCheck} label="Demo Booked" value={k.demo_booked} sub={`vs target ${k.demo_target}`} color="bg-green-100 text-green-600 dark:bg-green-500/15" />
      </div>

      <Card className="mt-6 border shadow-none">
        <div className="p-5 border-b flex items-center gap-2">
          <LiveDot /><h2 className="font-semibold font-display">Live Calls</h2>
          <span className="text-xs text-slate-400 ml-auto">Auto-refreshing</span>
        </div>
        {live.length === 0 ? (
          <EmptyState icon={Phone} title="No active calls" description="Next outbound window starts at 12:00 PM IST. Inbound line is live 24/7." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b"><tr>
                <th className="text-left px-5 py-3">Direction</th><th className="text-left py-3">Lead</th><th className="text-left py-3">City</th><th className="text-left py-3">Duration</th><th className="text-left py-3">Language</th><th className="text-left py-3">Status</th>
              </tr></thead>
              <tbody>
                {live.map((c) => (
                  <tr key={c.id} className="border-b last:border-0" data-testid={`live-call-${c.id}`}>
                    <td className="px-5 py-3"><DirectionBadge direction={c.direction} /></td>
                    <td className="py-3 font-medium">{c.name}</td>
                    <td className="py-3 text-slate-500">{c.city || "—"}</td>
                    <td className="py-3">{fmtDur(c.duration)}</td>
                    <td className="py-3 text-slate-500">{LANG_NAMES[c.language] || c.language}</td>
                    <td className="py-3"><span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-medium"><LiveDot /> {c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-2 border shadow-none">
          <div className="p-5 border-b"><h2 className="font-semibold font-display">Recent Activity</h2></div>
          <div className="divide-y">
            {activity.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-5 py-3" data-testid={`activity-${a.id}`}>
                <DirectionBadge direction={a.direction} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.name}</div>
                  <div className="text-xs text-slate-400">{fmtTime(a.time)}</div>
                </div>
                <span className="text-xs text-slate-500">{a.outcome}</span>
                <ScoreBadge score={a.score} />
              </div>
            ))}
            {activity.length === 0 && <div className="p-6 text-sm text-slate-400 text-center">No recent activity</div>}
          </div>
        </Card>

        <Card className="border shadow-none">
          <div className="p-5 border-b"><h2 className="font-semibold font-display">Today's Inbound Stats</h2></div>
          <div className="p-5 space-y-4">
            {[["Received", data.inbound_stats.received], ["Answered", data.inbound_stats.answered], ["Missed", data.inbound_stats.missed], ["Avg Wait", `${data.inbound_stats.avg_wait}s`]].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between">
                <span className="text-sm text-slate-500">{l}</span>
                <span className="font-semibold font-display">{v}</span>
              </div>
            ))}
            <div className="pt-3 border-t text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Next outbound window: 12:00 PM IST</div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
        {[["Best Outbound Hour", data.bottom.best_outbound_hour, TrendingUp], ["Top Inbound Hour", data.bottom.top_inbound_hour, Clock], ["Top City", data.bottom.top_city, MapPin], ["Top Interest", data.bottom.top_interest, Flame], ["Avg Duration", data.bottom.avg_duration, Phone]].map(([l, v, Ic]) => (
          <Card key={l} className="p-4 border shadow-none">
            <Ic className="w-4 h-4 text-indigo-500 mb-2" />
            <div className="text-sm font-semibold font-display truncate">{v}</div>
            <div className="text-[11px] text-slate-400">{l}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
